import { useState, useEffect, useCallback } from 'react';
import notify, { MESSAGES } from '../../../utils/notify';
import { api } from '../../../config';
import { getAuthToken, isAuthenticated } from '../../../auth/tokenvalidation';
import { unwrapTenderOpeningList } from '../utils/tenderOpeningHelpers';

export const buildTenderOpeningListUrl = ({
  currentPage,
  rowsPerPage,
  procurementTypeHeader,
  search,
  tenderOpeningStatus,
  deadlineStatus,
  sortField,
  sortDirection,
}) => {
  const params = new URLSearchParams();
  params.set('PageNumber', String(currentPage));
  params.set('PageSize', String(rowsPerPage));

  if (procurementTypeHeader) {
    params.set('ProcurementTypeHeader', procurementTypeHeader);
  }

  if (search?.trim()) {
    params.set('Search', search.trim());
  }

  if (
    tenderOpeningStatus !== '' &&
    tenderOpeningStatus !== null &&
    tenderOpeningStatus !== undefined
  ) {
    params.set('TenderOpeningStatus', String(tenderOpeningStatus));
  }

  if (deadlineStatus) params.set('DeadlineStatus', deadlineStatus);

  if (sortField && sortField !== 'updateDate') {
    params.set('SortBy', sortField);
    params.set('IsDescending', sortDirection === 'desc' ? 'true' : 'false');
  }

  return `${api.API_URL}/procurement/get-all-tender-opening?${params.toString()}`;
};

// "Last Update" is derived on the client, so that sort happens here too.
const sortByUpdateDate = (items, sortField, sortDirection) =>
  sortField === 'updateDate'
    ? [...items].sort((a, b) => {
        const left = a.updateDateRaw || 0;
        const right = b.updateDateRaw || 0;
        return sortDirection === 'asc' ? left - right : right - left;
      })
    : items;

const fetchTenderOpeningPage = async (url, token) => {
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    credentials: 'same-origin',
  });
  if (!response.ok) {
    throw new Error(`HTTP error! Status: ${response.status}`);
  }
  return unwrapTenderOpeningList(await response.json());
};

const useTenderOpeningList = ({
  procurementTypeHeader,
  refreshTrigger = 0,
  enabled = true,
  deadlineStatus = '',
}) => {
  const [data, setData] = useState({ items: [] });
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const [tenderOpeningStatusFilter, setTenderOpeningStatusFilter] =
    useState('');
  const [sortField, setSortField] = useState('procurementnumber');
  const [sortDirection, setSortDirection] = useState('desc');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    setCurrentPage(1);
  }, [deadlineStatus]);

  const fetchData = useCallback(async () => {
    void refreshTrigger;
    if (!enabled) return;

    const token = getAuthToken();
    if (!token || !isAuthenticated()) {
      notify.error(MESSAGES.authRequired);
      return;
    }

    setIsLoading(true);

    try {
      const url = buildTenderOpeningListUrl({
        currentPage,
        rowsPerPage,
        procurementTypeHeader,
        search: debouncedSearchText,
        tenderOpeningStatus: tenderOpeningStatusFilter,
        deadlineStatus,
        sortField,
        sortDirection,
      });

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        credentials: 'same-origin',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const result = await response.json();
      const { items, totalItems } = unwrapTenderOpeningList(result);

      setData({ items: sortByUpdateDate(items, sortField, sortDirection) });
      setTotalRows(totalItems);
    } catch (error) {
      notify.apiError(error, 'load tender opening data');
      setData({ items: [] });
      setTotalRows(0);
    } finally {
      setIsLoading(false);
    }
  }, [
    currentPage,
    rowsPerPage,
    debouncedSearchText,
    tenderOpeningStatusFilter,
    deadlineStatus,
    sortField,
    sortDirection,
    procurementTypeHeader,
    enabled,
    refreshTrigger,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearch = e => setSearchText(e.target.value);

  const handleStatusFilter = value => {
    setTenderOpeningStatusFilter(value);
    setCurrentPage(1);
  };

  const handlePageChange = page => setCurrentPage(page);

  const handlePerRowsChange = newPerPage => {
    setRowsPerPage(newPerPage);
    setCurrentPage(1);
  };

  const handleSort = (column, direction) => {
    const fieldMapping = {
      idProc: 'procurementnumber',
      submittedDate: 'submitteddatetenderopening',
      updateDate: 'updateDate',
      title: 'titleprocurement',
      period: 'contracttype',
      procType: 'procurementtype',
      status: 'tenderopeningstatus',
    };
    setSortField(fieldMapping[column.id] || column.id || 'procurementnumber');
    setSortDirection(direction);
  };

  const clearFilters = () => {
    setSearchText('');
    setTenderOpeningStatusFilter('');
    setSortField('procurementnumber');
    setSortDirection('desc');
    setCurrentPage(1);
  };

  // Every row matching the current filters and sort, for export.
  const fetchAllRows = async () => {
    const token = getAuthToken();
    if (!token || !isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const query = {
      currentPage: 1,
      procurementTypeHeader,
      search: debouncedSearchText,
      tenderOpeningStatus: tenderOpeningStatusFilter,
      deadlineStatus,
      sortField,
      sortDirection,
    };
    let page = await fetchTenderOpeningPage(
      buildTenderOpeningListUrl({
        ...query,
        rowsPerPage: Math.max(totalRows, 1),
      }),
      token
    );
    if (page.totalItems > page.items.length) {
      page = await fetchTenderOpeningPage(
        buildTenderOpeningListUrl({ ...query, rowsPerPage: page.totalItems }),
        token
      );
    }
    return sortByUpdateDate(page.items, sortField, sortDirection);
  };

  const startEntry = totalRows === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const endEntry = Math.min(currentPage * rowsPerPage, totalRows);

  return {
    data,
    totalRows,
    currentPage,
    rowsPerPage,
    searchText,
    tenderOpeningStatusFilter,
    isLoading,
    startEntry,
    endEntry,
    handleSearch,
    handleStatusFilter,
    handlePageChange,
    handlePerRowsChange,
    handleSort,
    clearFilters,
    fetchAllRows,
    refresh: fetchData,
  };
};

export default useTenderOpeningList;
